import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, BookMarked, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEnhancedToast } from '@/hooks/use-enhanced-toast';
import { parseLibraryExport, type ParsedImportBook, type ImportSource } from '@/services/libraryImport';
import { getTransmissions, importTransmissions, type ImportResult } from '@/services/transmissionsService';

/**
 * Import a Goodreads or StoryGraph library export into the user's transmissions
 * — the moment their Neural Map first lights up with real reading history.
 *
 * The flow is deliberately a *staged, confirmed* one because the write lands in
 * the transmissions table the iOS app shares: choose a file → parse it locally
 * → PREVIEW how many are new versus already in your library → only then, on an
 * explicit click, write. Every row is scoped to the signed-in user and mirrors
 * a manual "Log Signal" add, so an imported book is indistinguishable from a
 * hand-added one (see importTransmissions).
 */

interface ImportLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful import so the caller can refresh its list/map. */
  onImported: () => void;
}

type Stage = 'idle' | 'preview' | 'importing' | 'done';

const SOURCE_LABEL: Record<ImportSource, string> = {
  goodreads: 'Goodreads',
  storygraph: 'The StoryGraph',
};

const dedupeKey = (title: string, author: string) =>
  `${title.trim().toLowerCase()}|${author.trim().toLowerCase()}`;

const ImportLibraryModal = ({ isOpen, onClose, onImported }: ImportLibraryModalProps) => {
  const [stage, setStage] = useState<Stage>('idle');
  const [source, setSource] = useState<ImportSource | null>(null);
  const [newBooks, setNewBooks] = useState<ParsedImportBook[]>([]);
  const [dupCount, setDupCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useEnhancedToast();

  const reset = useCallback(() => {
    setStage('idle');
    setSource(null);
    setNewBooks([]);
    setDupCount(0);
    setBusy(false);
    setProgress({ done: 0, total: 0 });
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    if (busy) return; // don't let a click during the write orphan it
    reset();
    onClose();
  }, [busy, reset, onClose]);

  const handleFile = useCallback(
    async (file: File) => {
      setBusy(true);
      try {
        const text = await file.text();
        const parsed = parseLibraryExport(text);
        if (!parsed || parsed.books.length === 0) {
          toast({
            title: "Couldn't read that file",
            description: 'Upload the CSV exported from Goodreads or The StoryGraph.',
            variant: 'destructive',
          });
          return;
        }

        // Compute new-vs-duplicate against the existing library for the preview.
        const existing = await getTransmissions();
        const seen = new Set(existing.map((b) => dedupeKey(b.title, b.author)));
        const fresh: ParsedImportBook[] = [];
        let dups = 0;
        for (const b of parsed.books) {
          if (!b.title.trim() || !b.author.trim()) continue;
          const key = dedupeKey(b.title, b.author);
          if (seen.has(key)) {
            dups++;
          } else {
            seen.add(key);
            fresh.push(b);
          }
        }

        setSource(parsed.source);
        setNewBooks(fresh);
        setDupCount(dups);
        setStage('preview');
      } catch (err) {
        console.error('Import parse failed:', err);
        toast({ title: 'Import failed', description: 'Could not read that file.', variant: 'destructive' });
      } finally {
        setBusy(false);
      }
    },
    [toast]
  );

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = ''; // allow re-selecting the same file
  };

  const runImport = useCallback(async () => {
    if (newBooks.length === 0) return;
    setStage('importing');
    setBusy(true);
    setProgress({ done: 0, total: newBooks.length });
    try {
      const res = await importTransmissions(newBooks, (done, total) => setProgress({ done, total }));
      setResult(res);
      setStage('done');
      if (res.imported > 0) onImported();
    } catch (err) {
      console.error('Import failed:', err);
      toast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
      setStage('preview');
    } finally {
      setBusy(false);
    }
  }, [newBooks, onImported, toast]);

  if (!isOpen) return null;

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  const modal = (
    <div
      className="fixed inset-0 z-[2000] flex h-[100dvh] w-screen items-center justify-center bg-background/60 backdrop-blur-sm p-3"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="bg-slate-800/95 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl max-h-[calc(100dvh-1.5rem)] flex flex-col">
        <div className="relative p-3 border-b border-slate-700 flex items-center justify-center flex-shrink-0">
          <h2 className="text-slate-200 font-medium text-sm">Import your library</h2>
          {!busy && (
            <button
              onClick={handleClose}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors p-1.5 rounded-lg hover:bg-slate-700/50 active:scale-95"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {stage === 'idle' && (
            <>
              <p className="text-sm text-slate-400 leading-relaxed">
                Bring your reading history across from Goodreads or The StoryGraph and light up your
                Neural Map. Nothing is added until you've seen the preview and confirmed.
              </p>

              <label
                className={`flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-600 bg-slate-900/40 px-4 py-8 text-center transition-colors hover:border-cyan-400/40 ${
                  busy ? 'opacity-60' : 'cursor-pointer'
                }`}
              >
                {busy ? (
                  <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
                ) : (
                  <Upload className="w-6 h-6 text-slate-400" />
                )}
                <span className="text-sm text-slate-300">{busy ? 'Reading file…' : 'Choose a CSV file'}</span>
                <span className="text-xs text-slate-500">Goodreads or StoryGraph export</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onFileInput}
                  disabled={busy}
                  className="hidden"
                />
              </label>

              <div className="text-xs text-slate-500 leading-relaxed space-y-1">
                <p className="text-slate-400 font-medium">Where's my export?</p>
                <p>
                  <span className="text-slate-300">Goodreads:</span> My Books → Import and export →
                  Export Library.
                </p>
                <p>
                  <span className="text-slate-300">StoryGraph:</span> account menu → Manage Your Data
                  → export.
                </p>
              </div>
            </>
          )}

          {stage === 'preview' && (
            <>
              <div className="flex items-center gap-2 text-sm text-slate-200">
                <BookMarked className="w-4 h-4 text-cyan-400" />
                <span>
                  Found <strong className="text-white">{newBooks.length + dupCount}</strong> books
                  {source ? ` in your ${SOURCE_LABEL[source]} export` : ''}.
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-cyan-400/20 bg-cyan-400/5 px-3 py-2.5">
                  <div className="text-xl font-semibold text-cyan-300 tabular-nums">{newBooks.length}</div>
                  <div className="text-[11px] text-slate-400">new to import</div>
                </div>
                <div className="rounded-lg border border-slate-700/50 bg-slate-800/40 px-3 py-2.5">
                  <div className="text-xl font-semibold text-slate-300 tabular-nums">{dupCount}</div>
                  <div className="text-[11px] text-slate-400">already in your library</div>
                </div>
              </div>

              {newBooks.length > 0 ? (
                <div className="rounded-lg border border-slate-700/40 bg-slate-900/40 max-h-40 overflow-y-auto scrollbar-hide divide-y divide-slate-800">
                  {newBooks.slice(0, 60).map((b, i) => (
                    <div key={`${b.title}-${i}`} className="px-3 py-1.5">
                      <div className="text-xs text-slate-200 truncate">{b.title}</div>
                      <div className="text-[10px] text-slate-500 truncate">{b.author}</div>
                    </div>
                  ))}
                  {newBooks.length > 60 && (
                    <div className="px-3 py-1.5 text-[10px] text-slate-500">
                      …and {newBooks.length - 60} more
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-400">
                  Everything in this file is already in your library — nothing to import.
                </p>
              )}

              <div className="flex gap-2 pt-1">
                <Button onClick={reset} variant="ghost" className="flex-1 h-10 text-sm text-slate-300">
                  Choose another file
                </Button>
                <Button
                  onClick={runImport}
                  variant="outline"
                  className="flex-1 h-10 text-sm active:scale-[0.98] transition-transform"
                  disabled={newBooks.length === 0}
                >
                  Import {newBooks.length > 0 ? newBooks.length : ''}
                </Button>
              </div>
            </>
          )}

          {stage === 'importing' && (
            <div className="py-6 space-y-4 text-center">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
              <div>
                <p className="text-sm text-slate-200">Adding to your library…</p>
                <p className="text-xs text-slate-500 tabular-nums">
                  {progress.done} / {progress.total}
                </p>
              </div>
              <div className="h-1.5 w-full rounded-full bg-slate-700/60 overflow-hidden">
                <div className="h-full bg-cyan-400 transition-all duration-300" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          {stage === 'done' && result && (
            <div className="py-4 space-y-4 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto" />
              <div>
                <p className="text-slate-100 font-medium">
                  Imported {result.imported} book{result.imported === 1 ? '' : 's'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {result.skippedDuplicates > 0 && `${result.skippedDuplicates} already in your library. `}
                  {result.failed > 0 && `${result.failed} couldn't be added. `}
                  Your Neural Map has updated.
                </p>
              </div>
              <Button onClick={handleClose} variant="outline" className="w-full h-10 text-sm">
                Done
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ImportLibraryModal;
